import { getLocaleString } from '@assets/i18n/config';
import { InputAdornment, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material/TextField/TextField';
import { getLocaleNumberSeparators } from '@utils/common';
import Decimal from 'decimal.js';
import React from 'react';
import NumberFormat from 'react-number-format';

type FormatterProps = {
  onChange: (event: {
    target: { valueCents: undefined | number; name: string; value: string };
  }) => void;
  name: string;
  decimalPlaces?: number;
  placeholder?: string;
};

type AmountFieldProps = TextFieldProps & {
  useFormatAmountValueCents?: boolean;
  decimalPlaces?: number;
  placeholder?: string;
};

const CurrencyFormatter = React.forwardRef<
  NumberFormat<string>,
  FormatterProps
>((props, ref) => {
  const { onChange, decimalPlaces = 2, placeholder, ...other } = props;
  const { thousandSeparator, decimalSeparator } = getLocaleNumberSeparators();

  return (
    <NumberFormat
      {...other}
      placeholder={placeholder}
      getInputRef={ref}
      onValueChange={(values, sourceInfo) => {
        if (sourceInfo.event?.type !== 'change') return;

        if (Number.isNaN(Number(values.value))) {
          return;
        }

        let formattedValue: string;
        if (values.value) {
          const num = Number(values.value);

          formattedValue =
            num % 1 === 0
              ? Math.abs(num).toString()
              : num.toFixed(decimalPlaces);
          const valueCents = new Decimal(Number(values.value) || 0)
            .times(100)
            .times(10 ** decimalPlaces)
            .round()
            .div(10 ** decimalPlaces)
            .toNumber();

          onChange({
            target: {
              name: props.name,
              value: formattedValue,
              valueCents: formattedValue === '' ? undefined : valueCents,
            },
          });
        } else {
          onChange({
            target: {
              name: props.name,
              value: '',
              valueCents: undefined,
            },
          });
        }
      }}
      decimalScale={decimalPlaces}
      thousandSeparator={thousandSeparator}
      decimalSeparator={decimalSeparator}
      isNumericString
      allowLeadingZeros={false}
    />
  );
});

const AmountField = ({
  useFormatAmountValueCents = false,
  decimalPlaces = 2,
  placeholder,
  ...props
}: AmountFieldProps) => {
  const symbolAfter = !getLocaleString().startsWith('en');

  const parseValue = (value: unknown): string => {
    if (value === undefined || value === '') {
      return '';
    }

    const dollars = Number(value) / 100;

    if (Number.isNaN(dollars)) {
      return '';
    }

    if (useFormatAmountValueCents) {
      return Number.isInteger(dollars)
        ? dollars.toString()
        : dollars.toFixed(decimalPlaces);
    }

    return dollars.toString();
  };

  const defaultPlaceholder = `0.${'0'.repeat(decimalPlaces)}`;

  return (
    <TextField
      {...props}
      inputProps={{
        ...(props.inputProps ?? {}),
        decimalPlaces,
        placeholder: placeholder ?? defaultPlaceholder,
      }}
      InputProps={{
        ...props.InputProps,
        inputComponent: CurrencyFormatter as any,
        startAdornment: symbolAfter ? undefined : (
          <InputAdornment position='start'>$</InputAdornment>
        ),
        endAdornment: symbolAfter ? (
          <InputAdornment position='end'>$</InputAdornment>
        ) : undefined,
      }}
      value={parseValue(props.value)}
    />
  );
};

export default AmountField;
